import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from './ToastContext';
import { LoadError } from './ui';

const API = '/api';

// Cosmetic-only cleanup of a "table.field" PII entry for display (e.g. "employee.company_email"
// -> "Employee: Company email"). The raw entry is never used for anything but display here -
// the backend independently re-validates every field name against its own allowlist before ever
// nulling it, so a display-only formatting bug here cannot affect what gets deleted.
const formatPiiField = (entry) => {
  if (entry.startsWith('employee_family (')) return 'Family members: name / date of birth';
  const [table, field] = entry.includes('.') ? entry.split('.', 2) : [null, entry];
  const tableLabel = { employee: 'Employee', leaver_form: 'Leaver form', sponsor_form: 'Sponsor form', new_starter_form: 'New starter form' }[table] || table;
  const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return tableLabel ? `${tableLabel}: ${fieldLabel}` : fieldLabel;
};

// Why a given item was flagged (region + period) - shown alongside the flag/document/PII detail
// so Compliance can see the reasoning, not just the flag itself.
const formatRetentionReason = (f) => {
  if (f.retention_group === 'long') return `Long-term data · ${f.retention_years} years (${f.region})`;
  if (f.retention_group === 'short') return `Short-term data · ${f.retention_years} year (all regions)`;
  return null;
};

const ATTESTATION_TABLE_LABELS = {
  outside_affiliations: 'Outside Affiliation',
  personal_account_deals: 'Personal Account Deal',
  gifts_entertainment: 'Gift / Entertainment',
  public_officials: 'Public Official Engagement',
};

function RetentionReview({ currentUser, userRole, onBack }) {
  const showToast = useToast();
  const [flags, setFlags] = useState({
    six_year_expiry: [], short_retention_expiry: [], attestation_expiry: [], no_leaver_date: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // "Confirm and delete" modal state - a single object holding the flag being confirmed, or null.
  // acknowledged must be explicitly checked before the modal's own delete button is enabled -
  // this is a SEPARATE, additional step beyond opening the modal itself, so deletion can never
  // happen from a single click anywhere in the UI. Shared by six_year_expiry (long-term) and
  // short_retention_expiry (short-term) flags - both have the identical document/PII shape, so
  // the same modal renders either correctly with no branching needed.
  const [deleteModalFlag, setDeleteModalFlag] = useState(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  // Separate "Confirm and delete" modal for attestation_expiry flags - same 2-step
  // acknowledge-then-delete mechanic as above, kept as its own state/modal (not shoehorned into
  // the one above) because attestation records have a different shape (attestation_records /
  // attestation_count, not document_types / pii_fields).
  const [deleteAttestationModalFlag, setDeleteAttestationModalFlag] = useState(null);
  const [deleteAttestationAcknowledged, setDeleteAttestationAcknowledged] = useState(false);

  // "Assign last working date" modal state (no_leaver_date case, path A).
  const [assignDateModalFlag, setAssignDateModalFlag] = useState(null);
  const [assignDateValue, setAssignDateValue] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/retention/flags`)
      .then(res => { setFlags(res.data); setLoading(false); })
      .catch(() => { setError('Could not load retention flags.'); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const closeDeleteModal = () => { setDeleteModalFlag(null); setDeleteAcknowledged(false); };

  const confirmDelete = async () => {
    if (!deleteModalFlag || !deleteAcknowledged) return;
    setActionLoadingId(deleteModalFlag.id);
    try {
      await axios.post(`${API}/retention/flags/${deleteModalFlag.id}/confirm-delete`, { confirm: true });
      showToast('Records permanently deleted.', 'success');
      closeDeleteModal();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Deletion failed. Please try again.', 'error');
    }
    setActionLoadingId(null);
  };

  const closeDeleteAttestationModal = () => { setDeleteAttestationModalFlag(null); setDeleteAttestationAcknowledged(false); };

  const confirmDeleteAttestation = async () => {
    if (!deleteAttestationModalFlag || !deleteAttestationAcknowledged) return;
    setActionLoadingId(deleteAttestationModalFlag.id);
    try {
      await axios.post(`${API}/retention/flags/${deleteAttestationModalFlag.id}/confirm-delete`, { confirm: true });
      showToast('Attestation records permanently deleted.', 'success');
      closeDeleteAttestationModal();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Deletion failed. Please try again.', 'error');
    }
    setActionLoadingId(null);
  };

  const closeAssignDateModal = () => { setAssignDateModalFlag(null); setAssignDateValue(''); };

  const confirmAssignDate = async () => {
    if (!assignDateModalFlag || !assignDateValue) return;
    setActionLoadingId(assignDateModalFlag.id);
    try {
      await axios.post(`${API}/retention/flags/${assignDateModalFlag.id}/assign-date`, { last_working_date: assignDateValue });
      showToast('Last working date assigned.', 'success');
      closeAssignDateModal();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not assign date. Please try again.', 'error');
    }
    setActionLoadingId(null);
  };

  const resolveOrphan = async (flag) => {
    setActionLoadingId(flag.id);
    try {
      await axios.post(`${API}/retention/flags/${flag.id}/resolve`);
      showToast('Marked as reviewed - no action taken.', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not resolve. Please try again.', 'error');
    }
    setActionLoadingId(null);
  };

  if (!['compliance', 'coo', 'admin'].includes(userRole)) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <p style={styles.accessDeniedTitle}>Access Restricted</p>
          <p style={styles.accessDeniedMsg}>This screen is only accessible to Compliance, COO, and Admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {onBack && (
        <button style={styles.backBtn} onClick={onBack}
          onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#1a1a2e'; }}>
          ← Back to dashboard
        </button>
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>Data Retention Review</h1>
        <p style={styles.subtitle}>
          Compliance-confirmed retention policy: long-term data (6 years UK / 7 years non-UK),
          short-term data (1 year, every region) and attestation records (7 years, every region) -
          all measured from the day after the employee's last working date. Deletion is permanent
          and requires explicit confirmation for every individual record - nothing is ever deleted
          automatically.
        </p>
      </div>

      {loading ? (
        <p style={styles.empty}>Loading...</p>
      ) : error ? (
        // RETFAILSAFE: a failed load must never render the four "No employees..." compliance
        // assertions below, which is exactly what happened when `error` was shown ALONGSIDE the
        // sections instead of INSTEAD OF them - flags stayed at its empty-array default, so a
        // fetch failure and a genuinely clean retention state looked identical except for a
        // message easy to miss above four confident empty-state sentences. LoadError with a
        // retry, following the shared pattern, replaces the sections entirely on failure.
        <LoadError message={error} onRetry={load} />
      ) : (
        <>
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>
              Long-Term Data: Past Retention Window (6yr UK / 7yr non-UK)
              {flags.six_year_expiry.length > 0 && <span style={styles.badge}>{flags.six_year_expiry.length}</span>}
            </h2>
            {flags.six_year_expiry.length === 0 ? (
              <p style={styles.empty}>No employees are currently past the long-term retention window.</p>
            ) : (
              flags.six_year_expiry.map(f => (
                <div key={f.id} style={styles.flagRow}>
                  <div style={styles.flagRowHeader}>
                    <div>
                      <p style={styles.employeeName}>{f.employee_name}</p>
                      <p style={styles.flagMeta}>Last working date: {f.last_working_date || 'Unknown'}</p>
                      <p style={styles.flagReason}>{formatRetentionReason(f)}</p>
                    </div>
                    <button style={styles.dangerBtn} disabled={actionLoadingId === f.id}
                      onClick={() => { setDeleteModalFlag(f); setDeleteAcknowledged(false); }}>
                      Confirm and delete
                    </button>
                  </div>
                  <div style={styles.flagDetail}>
                    <div>
                      <p style={styles.detailLabel}>Documents flagged ({f.document_count})</p>
                      {f.document_count === 0 ? <p style={styles.detailEmpty}>None</p> : (
                        <div style={styles.chipRow}>
                          {f.document_types.map((t, i) => <span key={`${t}-${i}`} style={styles.chip}>{t.replace(/_/g, ' ')}</span>)}
                        </div>
                      )}
                    </div>
                    <div>
                      <p style={styles.detailLabel}>Personal data fields flagged ({f.pii_fields.length})</p>
                      {f.pii_fields.length === 0 ? <p style={styles.detailEmpty}>None</p> : (
                        <div style={styles.chipRow}>
                          {f.pii_fields.map((p, i) => <span key={`${p}-${i}`} style={styles.chip}>{formatPiiField(p)}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>
              Short-Term Data: Past Retention Window (1 year, all regions)
              {flags.short_retention_expiry.length > 0 && <span style={styles.badge}>{flags.short_retention_expiry.length}</span>}
            </h2>
            {flags.short_retention_expiry.length === 0 ? (
              <p style={styles.empty}>No employees are currently past the short-term retention window.</p>
            ) : (
              flags.short_retention_expiry.map(f => (
                <div key={f.id} style={styles.flagRow}>
                  <div style={styles.flagRowHeader}>
                    <div>
                      <p style={styles.employeeName}>{f.employee_name}</p>
                      <p style={styles.flagMeta}>Last working date: {f.last_working_date || 'Unknown'}</p>
                      <p style={styles.flagReason}>{formatRetentionReason(f)}</p>
                    </div>
                    <button style={styles.dangerBtn} disabled={actionLoadingId === f.id}
                      onClick={() => { setDeleteModalFlag(f); setDeleteAcknowledged(false); }}>
                      Confirm and delete
                    </button>
                  </div>
                  <div style={styles.flagDetail}>
                    <div>
                      <p style={styles.detailLabel}>Documents flagged ({f.document_count})</p>
                      {f.document_count === 0 ? <p style={styles.detailEmpty}>None</p> : (
                        <div style={styles.chipRow}>
                          {f.document_types.map((t, i) => <span key={`${t}-${i}`} style={styles.chip}>{t.replace(/_/g, ' ')}</span>)}
                        </div>
                      )}
                    </div>
                    <div>
                      <p style={styles.detailLabel}>Personal data fields flagged ({f.pii_fields.length})</p>
                      {f.pii_fields.length === 0 ? <p style={styles.detailEmpty}>None</p> : (
                        <div style={styles.chipRow}>
                          {f.pii_fields.map((p, i) => <span key={`${p}-${i}`} style={styles.chip}>{formatPiiField(p)}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>
              Attestation Records: Past Retention Window (7 years, all regions)
              {flags.attestation_expiry.length > 0 && <span style={styles.badge}>{flags.attestation_expiry.length}</span>}
            </h2>
            {flags.attestation_expiry.length === 0 ? (
              <p style={styles.empty}>No employees currently have attestation records past the 7-year retention window.</p>
            ) : (
              flags.attestation_expiry.map(f => (
                <div key={f.id} style={styles.flagRow}>
                  <div style={styles.flagRowHeader}>
                    <div>
                      <p style={styles.employeeName}>{f.employee_name}</p>
                      <p style={styles.flagMeta}>Last working date: {f.last_working_date || 'Unknown'}</p>
                      <p style={styles.flagReason}>Attestation records · {f.retention_years} years (all regions)</p>
                    </div>
                    <button style={styles.dangerBtn} disabled={actionLoadingId === f.id}
                      onClick={() => { setDeleteAttestationModalFlag(f); setDeleteAttestationAcknowledged(false); }}>
                      Confirm and delete
                    </button>
                  </div>
                  <div style={styles.flagDetail}>
                    <div>
                      <p style={styles.detailLabel}>Attestation records flagged ({f.attestation_count})</p>
                      {f.attestation_count === 0 ? <p style={styles.detailEmpty}>None</p> : (
                        <div style={styles.chipRow}>
                          {f.attestation_records.map((r, i) => (
                            <span key={`${r.table}-${r.id}-${i}`} style={styles.chip}>{ATTESTATION_TABLE_LABELS[r.table] || r.table}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>
              No Leaver Date on File
              {flags.no_leaver_date.length > 0 && <span style={styles.badgeNeutral}>{flags.no_leaver_date.length}</span>}
            </h2>
            <p style={styles.hint}>
              These employees are archived but have no usable last working date, so none of the
              retention periods above can be evaluated. No deletion is possible for these until a
              date is assigned.
            </p>
            {flags.no_leaver_date.length === 0 ? (
              <p style={styles.empty}>No orphan cases.</p>
            ) : (
              flags.no_leaver_date.map(f => (
                <div key={f.id} style={styles.flagRow}>
                  <div style={styles.flagRowHeader}>
                    <div>
                      <p style={styles.employeeName}>{f.employee_name}</p>
                      <p style={styles.flagMeta}>No last working date could be determined.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={styles.secondaryBtn} disabled={actionLoadingId === f.id}
                        onClick={() => { setAssignDateModalFlag(f); setAssignDateValue(''); }}>
                        Assign date
                      </button>
                      <button style={styles.secondaryBtn} disabled={actionLoadingId === f.id}
                        onClick={() => resolveOrphan(f)}>
                        Mark reviewed (no action)
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Delete confirmation modal - requires the acknowledgment checkbox before the danger
          button inside the modal is even enabled, in addition to the initial "Confirm and
          delete" click that opened it. Two deliberate steps, never one accidental click. */}
      {deleteModalFlag && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Permanently delete records</h2>
            <p style={styles.modalText}>
              You are about to <strong>permanently delete</strong> the following for{' '}
              <strong>{deleteModalFlag.employee_name}</strong>. This action <strong>cannot be undone</strong>.
            </p>
            <div style={styles.modalDetail}>
              <p style={styles.detailLabel}>{deleteModalFlag.document_count} document(s):</p>
              <div style={styles.chipRow}>
                {deleteModalFlag.document_types.length === 0
                  ? <span style={styles.detailEmpty}>None</span>
                  : deleteModalFlag.document_types.map((t, i) => <span key={`${t}-${i}`} style={styles.chip}>{t.replace(/_/g, ' ')}</span>)}
              </div>
              <p style={{ ...styles.detailLabel, marginTop: '12px' }}>{deleteModalFlag.pii_fields.length} personal data field(s):</p>
              <div style={styles.chipRow}>
                {deleteModalFlag.pii_fields.length === 0
                  ? <span style={styles.detailEmpty}>None</span>
                  : deleteModalFlag.pii_fields.map((p, i) => <span key={`${p}-${i}`} style={styles.chip}>{formatPiiField(p)}</span>)}
              </div>
            </div>
            <label style={styles.ackLabel}>
              <input type="checkbox" checked={deleteAcknowledged} onChange={e => setDeleteAcknowledged(e.target.checked)} />
              I understand this action is permanent and cannot be undone.
            </label>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={closeDeleteModal}>Cancel</button>
              <button
                style={{ ...styles.dangerBtn, opacity: deleteAcknowledged ? 1 : 0.5, cursor: deleteAcknowledged ? 'pointer' : 'not-allowed' }}
                disabled={!deleteAcknowledged || actionLoadingId === deleteModalFlag.id}
                onClick={confirmDelete}>
                {actionLoadingId === deleteModalFlag.id ? 'Deleting...' : 'Yes, permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attestation records delete confirmation modal - same 2-step acknowledge-then-delete
          mechanic as the modal above, kept separate since attestation records have a different
          shape (attestation_records, not document_types/pii_fields). */}
      {deleteAttestationModalFlag && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Permanently delete attestation records</h2>
            <p style={styles.modalText}>
              You are about to <strong>permanently delete</strong> the following for{' '}
              <strong>{deleteAttestationModalFlag.employee_name}</strong>. This action <strong>cannot be undone</strong>.
            </p>
            <div style={styles.modalDetail}>
              <p style={styles.detailLabel}>{deleteAttestationModalFlag.attestation_count} attestation record(s):</p>
              <div style={styles.chipRow}>
                {deleteAttestationModalFlag.attestation_records.length === 0
                  ? <span style={styles.detailEmpty}>None</span>
                  : deleteAttestationModalFlag.attestation_records.map((r, i) => (
                    <span key={`${r.table}-${r.id}-${i}`} style={styles.chip}>{ATTESTATION_TABLE_LABELS[r.table] || r.table}</span>
                  ))}
              </div>
            </div>
            <label style={styles.ackLabel}>
              <input type="checkbox" checked={deleteAttestationAcknowledged} onChange={e => setDeleteAttestationAcknowledged(e.target.checked)} />
              I understand this action is permanent and cannot be undone.
            </label>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={closeDeleteAttestationModal}>Cancel</button>
              <button
                style={{ ...styles.dangerBtn, opacity: deleteAttestationAcknowledged ? 1 : 0.5, cursor: deleteAttestationAcknowledged ? 'pointer' : 'not-allowed' }}
                disabled={!deleteAttestationAcknowledged || actionLoadingId === deleteAttestationModalFlag.id}
                onClick={confirmDeleteAttestation}>
                {actionLoadingId === deleteAttestationModalFlag.id ? 'Deleting...' : 'Yes, permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign last working date modal (orphan case, path A) - never deletes anything. */}
      {assignDateModalFlag && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Assign last working date</h2>
            <p style={styles.modalText}>
              For <strong>{assignDateModalFlag.employee_name}</strong>. This routes the employee into
              the normal retention evaluation going forward - nothing is deleted now.
            </p>
            <input type="date" style={styles.dateInput} value={assignDateValue}
              onChange={e => setAssignDateValue(e.target.value)} />
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={closeAssignDateModal}>Cancel</button>
              <button style={styles.primaryBtn} disabled={!assignDateValue || actionLoadingId === assignDateModalFlag.id}
                onClick={confirmAssignDate}>
                {actionLoadingId === assignDateModalFlag.id ? 'Saving...' : 'Assign date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '960px', margin: '0 auto', padding: '24px' },
  backBtn: { background: 'none', border: '1px solid rgba(26,39,68,0.25)', color: '#1a1a2e', fontSize: '14px', cursor: 'pointer', padding: '9px 18px', borderRadius: '6px', marginBottom: '24px', fontWeight: '600' },
  header: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 6px 0' },
  subtitle: { fontSize: '13px', color: '#888', margin: 0, maxWidth: '640px', lineHeight: 1.5 },
  error: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#c0392b', marginBottom: '16px' },
  empty: { fontSize: '13px', color: '#999', margin: '8px 0' },
  hint: { fontSize: '12px', color: '#888', margin: '0 0 12px 0', lineHeight: 1.5 },
  sectionCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '10px' },
  badge: { backgroundColor: '#c0392b', color: '#fff', fontSize: '12px', fontWeight: '600', borderRadius: '10px', padding: '1px 8px' },
  badgeNeutral: { backgroundColor: '#c9a84c', color: '#1a1a2e', fontSize: '12px', fontWeight: '600', borderRadius: '10px', padding: '1px 8px' },
  flagRow: { border: '1px solid #eee', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px' },
  flagRowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
  employeeName: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 2px 0' },
  flagMeta: { fontSize: '12px', color: '#888', margin: 0 },
  flagReason: { fontSize: '12px', color: '#c9a84c', fontWeight: '600', margin: '2px 0 0 0' },
  flagDetail: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f3f3f3' },
  detailLabel: { fontSize: '11px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 6px 0' },
  detailEmpty: { fontSize: '12px', color: '#aaa' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip: { backgroundColor: '#f0f4f8', border: '1px solid #ddd', borderRadius: '12px', padding: '3px 10px', fontSize: '12px', color: '#333' },
  dangerBtn: { backgroundColor: '#c0392b', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: '500' },
  secondaryBtn: { backgroundColor: '#fff', color: '#1a1a2e', border: '1px solid #1a1a2e', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  primaryBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' },
  cancelBtn: { backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: '8px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', padding: '28px', width: '520px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#c0392b', margin: '0 0 12px 0' },
  modalText: { fontSize: '13px', color: '#333', lineHeight: 1.6, margin: '0 0 16px 0' },
  modalDetail: { backgroundColor: '#fdf2f2', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px' },
  ackLabel: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#1a1a2e', fontWeight: '500', marginBottom: '20px', cursor: 'pointer' },
  modalActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  dateInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '20px' },
  accessDenied: { textAlign: 'center', padding: '60px 20px' },
  accessDeniedTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px 0' },
  accessDeniedMsg: { fontSize: '14px', color: '#888', margin: 0 },
};

export default RetentionReview;
